import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { HospitalNames } from '@shared/models/hospitalTypes.model';
import { ErrorService } from '@shared/services/error.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { ScrollbarsAutoHideBehavior } from 'overlayscrollbars';
import { UserService } from 'src/app/shared/services/user.service';
import { Profile } from '../../models/profile.model';
import { TOPICS } from './../../const/globals.const';
import { NotificationService } from './../../services/notification.service';
import { InstanceIdService } from '@shared/services/instance-id.service';

@Component({
  selector: 'app-notifications-feed',
  templateUrl: './notifications-feed.component.html',
  styleUrls: ['./notifications-feed.component.scss'],
  standalone: false,
})
export class NotificationsFeedComponent implements OnInit {
  private instanceId: string;

  private static instance: NotificationsFeedComponent;

  private profile: Profile | undefined;

  public notifications: any[] = [];

  private pageSize: number | undefined;

  private page = 1;

  public isLoading: boolean = true;

  public scrollLoading: boolean | undefined;

  private hasNextPage: any;

  public osComponentOptions = {
    className: 'os-theme-dark custom-sidebar',
    nativeScrollbarsOverlaid: {
      showNativeScrollbars: true,
    },
    callbacks: {
      onScroll: this.onScroll,
    },
    overflowBehavior: {
      x: 'hidden',
    },

    paddingAbsolute: true,
    scrollbars: {
      autoHide: 'never' as ScrollbarsAutoHideBehavior,
      clickScrolling: true,
    },
  };

  public isMultiHospitals: boolean = false;

  public searchKeyword: string = '';

  public hospitalNames: HospitalNames;

  constructor(
    public dialogRef: MatDialogRef<NotificationsFeedComponent>,
    private notificationService: NotificationService,
    private userService: UserService,
    private ChangeDetectorRef: ChangeDetectorRef,
    private errorService: ErrorService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.hospitalNames = this.userService.getHospitalNames();
    NotificationsFeedComponent.instance = this;
  }

  ngOnInit() {
    this.isMultiHospitals = this.userService.isCurrentUserHasMultipleHospitalsSelected();
    this.pageSize = Math.round(window.innerHeight / 100) + 7;
    this.getCurrentProfile();
    if (this.profile && this.profile._id) {
      this.getNotifications();
    }
  }

  getCurrentProfile() {
    this.profile = this.userService.getCurrentUser().profile;
  }

  getNotifications() {
    this.subscriptionManager
      .createRequest(
        this.notificationService.getNotifications(
          this.profile!._id!,
          this.page,
          this.pageSize ?? Math.round(window.innerHeight / 100) + 7,
          this.searchKeyword
        ),
        'getNotifications',
        this.instanceId
      )
      .subscribe({
        next: (res: any) => {
          res.notifications?.forEach((notification: any) => {
            if (notification.hospital && notification.hospital._id) {
              notification.hospital.displayName = this.userService.resolveHospitalName(notification.hospital._id);
            }
          });
          this.notifications = this.notifications.concat(res.notifications);
          if (!res || !res.notifications || !res.notifications.length) {
            this.notifications = [];
          }
          this.addDateToSmartBriefBody(this.notifications);
          this.getIcons();
          this.isLoading = false;
          this.scrollLoading = false;
          this.hasNextPage = res.hasNextPage;
          this.page++;
          if (!(this.ChangeDetectorRef as any).destroyed) {
            this.ChangeDetectorRef.detectChanges();
          }
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  getIcons() {
    this.notifications = this.notifications.map((notification: any) => {
      if (notification.topic == 'update-store') {
        notification.topic = 'update';
      }
      return {
        ...notification,
        icon: `${this.getIcon(notification.topic)}`,
      };
    });
  }

  getIcon(topic: string) {
    const {
      CALENDAR,
      VACATIONS,
      SMART_BRIEF,
      PARCOURS_IBODE_REQUEST,
      PARCOURS_IBODE_RECAP,
      PROGRAM,
      STATOP,
      MESSAGERIE,
      UPDATE_STORE,
    } = TOPICS;
    if (topic == CALENDAR || topic == VACATIONS) {
      return 'calendar_today';
    } else if (topic == SMART_BRIEF) {
      return 'phone_iphone';
    } else if (topic == PARCOURS_IBODE_REQUEST) {
      return 'route';
    } else if (topic == PARCOURS_IBODE_RECAP) {
      return 'route';
    } else if (topic == PROGRAM) {
      return 'auto_fix_high';
    } else if (topic == STATOP) {
      return 'insights';
    } else if (topic == MESSAGERIE) {
      return 'chat_bubble';
    } else if (topic == UPDATE_STORE || topic == 'update') {
      return 'system_update';
    }
    return 'notifications';
  }

  onScroll(event: any): void {
    const tableViewHeight = event.target.offsetHeight;
    const tableScrollHeight = event.target.scrollHeight;
    const scrollLocation = event.target.scrollTop;
    const buffer = 300;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (
      scrollLocation > limit &&
      NotificationsFeedComponent.instance.hasNextPage &&
      !NotificationsFeedComponent.instance.isLoading
    ) {
      NotificationsFeedComponent.instance.isLoading = true;
      NotificationsFeedComponent.instance.getNotifications();
    }
  }

  public search = (event: KeyboardEvent) => {
    const value = (event.target as HTMLInputElement).value;
    if (value !== '' && value.trim().length !== 0) {
      this.notifications = [];
      this.page = 1;
      this.searchKeyword = value;
      this.getNotifications();
    } else if (value == '') {
      this.page = 1;
      this.notifications = [];
      this.searchKeyword = '';
      this.getNotifications();
    }
  };

  addDateToSmartBriefBody(array: any[]) {
    const { SMART_BRIEF } = TOPICS;
    this.notifications = array.map((elet: any) => {
      if (elet.topic == SMART_BRIEF) {
        const bodyArr = elet.body.split("aujourd'hui");
        const dateString = new Date(elet.createdAt).toLocaleDateString('en-GB');
        elet.body = bodyArr[0] + "aujourd'hui (" + dateString + ')' + (bodyArr[1] ? bodyArr[1] : '');
      }
      return elet;
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
